import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { fetchCarbonIntensityData, CarbonIntensityResult } from '../../services/carbonIntensityService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface CarbonIntensityChartProps {
  geofips: string;
  locationName: string;
}

const CarbonIntensityChart: React.FC<CarbonIntensityChartProps> = ({ geofips, locationName }) => {
  const [data, setData] = useState<CarbonIntensityResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchCarbonIntensityData(geofips);
        setData(result);
      } catch (err) {
        console.error('Error loading carbon intensity data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load carbon intensity data');
      } finally {
        setLoading(false);
      }
    };

    if (geofips) {
      loadData();
    }
  }, [geofips]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading carbon intensity data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">No carbon intensity data available</div>
      </div>
    );
  }

  return (
    <div className="w-full">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Carbon Intensity of Electricity - {locationName}
          </h3>
        </div>
      
      <div className="h-96">
        <Bar data={data.chartData} options={data.chartOptions} />
      </div>
      
      {/* Custom Legend with Color Table */}
      <div className="mt-4 flex justify-center">
        <div className="text-sm">
          <table style={{ borderCollapse: 'collapse' }}>
            <tbody>
              {data.providers.cca.length > 0 && (
                <tr>
                  <td style={{ 
                    width: '24px', 
                    height: '16px', 
                    backgroundColor: '#10b981', 
                    border: '1px solid #d1d5db' 
                  }}></td>
                  <td style={{ paddingLeft: '12px', fontWeight: '500' }}>
                    {data.providers.cca.join(', ')} - Power Content Label
                  </td>
                </tr>
              )}
              {data.providers.egrid.length > 0 && (
                <tr>
                  <td style={{ 
                    width: '24px', 
                    height: '16px', 
                    backgroundColor: '#3b82f6', 
                    border: '1px solid #d1d5db' 
                  }}></td>
                  <td style={{ paddingLeft: '12px', fontWeight: '500' }}>
                    {data.providers.egrid.join(', ')} - EIA-861 & eGRID
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ 
                  width: '24px', 
                  height: '16px', 
                  backgroundColor: '#f59e0b', 
                  border: '1px solid #d1d5db' 
                }}></td>
                <td style={{ paddingLeft: '12px', fontWeight: '500' }}>
                  Interpolated - Calculated between observed points
                </td>
              </tr>
              <tr>
                <td style={{ 
                  width: '24px', 
                  height: '16px', 
                  backgroundColor: '#ef4444', 
                  border: '1px solid #d1d5db' 
                }}></td>
                <td style={{ paddingLeft: '12px', fontWeight: '500' }}>
                  Extrapolated - Trend-based projection
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default CarbonIntensityChart;
